import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider } from './context/SafeAppContext';
import { ToastProvider } from './context/ToastContext';
import './App.css';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton/ScrollToTopButton';
import BottomNav from './components/BottomNav/BottomNav';

import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Home from './views/Home/Home';

// Lazy load secondary routes to keep initial bundle light
const Shop = React.lazy(() => import('./views/Shop/Shop'));
const ProductDetail = React.lazy(() => import('./views/ProductDetail/ProductDetail'));
const Cart = React.lazy(() => import('./views/Cart/Cart'));
const Wishlist = React.lazy(() => import('./views/Wishlist/Wishlist'));
const Login = React.lazy(() => import('./views/Auth/Login'));
const Register = React.lazy(() => import('./views/Auth/Register'));
const ForgotPassword = React.lazy(() => import('./views/Auth/ForgotPassword'));
const Profile = React.lazy(() => import('./views/Profile/Profile'));
const Checkout = React.lazy(() => import('./views/Checkout/Checkout'));
const Payment = React.lazy(() => import('./views/Payment/Payment'));
const Orders = React.lazy(() => import('./views/Orders/Orders'));
const OrderDetails = React.lazy(() => import('./views/Orders/OrderDetails'));
const CategoryPage = React.lazy(() => import('./views/CategoryPage/CategoryPage'));
const About = React.lazy(() => import('./views/About/About'));
const ReturnPolicy = React.lazy(() => import('./views/Policies/ReturnPolicy'));
const RefundPolicy = React.lazy(() => import('./views/Policies/RefundPolicy'));
const PrivacyPolicy = React.lazy(() => import('./views/Policies/PrivacyPolicy'));
const Disclaimer = React.lazy(() => import('./views/Policies/Disclaimer'));
const AdminLogin = React.lazy(() => import('./views/Admin/AdminLogin'));
const AdminLayout = React.lazy(() => import('./views/Admin/AdminLayout'));
const Dashboard = React.lazy(() => import('./views/Admin/Dashboard'));
const AdminProducts = React.lazy(() => import('./views/Admin/Products'));
const ProductForm = React.lazy(() => import('./views/Admin/ProductForm'));
const AdminOrders = React.lazy(() => import('./views/Admin/Orders'));
const OrderDetail = React.lazy(() => import('./views/Admin/OrderDetail'));
const AdminUsers = React.lazy(() => import('./views/Admin/Users'));
const UserDetail = React.lazy(() => import('./views/Admin/UserDetail'));
const Sections = React.lazy(() => import('./views/Admin/Sections'));
const SectionForm = React.lazy(() => import('./views/Admin/SectionForm'));
// CMS Pages
const CMSDashboard = React.lazy(() => import('./views/Admin/CMS/CMSDashboard'));
const HeroManager = React.lazy(() => import('./views/Admin/CMS/HeroManager'));
const BannerManager = React.lazy(() => import('./views/Admin/CMS/BannerManager'));
const CategoryManager = React.lazy(() => import('./views/Admin/CMS/CategoryManager'));
const FeaturedCollectionManager = React.lazy(() => import('./views/Admin/CMS/FeaturedCollectionManager'));
const HomepageManager = React.lazy(() => import('./views/Admin/CMS/HomepageManager'));
const AboutManager = React.lazy(() => import('./views/Admin/CMS/AboutManager'));
const TestimonialManager = React.lazy(() => import('./views/Admin/CMS/TestimonialManager'));
const NewsletterManager = React.lazy(() => import('./views/Admin/CMS/NewsletterManager'));
const CouponManager = React.lazy(() => import('./views/Admin/CMS/CouponManager'));
const FooterManager = React.lazy(() => import('./views/Admin/CMS/FooterManager'));
const NavigationManager = React.lazy(() => import('./views/Admin/CMS/NavigationManager'));
const MediaLibrary = React.lazy(() => import('./views/Admin/CMS/MediaLibrary'));
const SiteSettings = React.lazy(() => import('./views/Admin/CMS/SiteSettings'));

// Loading component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    flexDirection: 'column'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #E5E7EB',
      borderTop: '4px solid #1F2937',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '20px'
    }}></div>
    <p>Loading...</p>
  </div>
);

// Component to conditionally show Header/Footer
const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isHomePage = location.pathname === '/';

  return (
    <div className="App">
      {!isAdminRoute && <Header />}
      <main className={isHomePage ? 'home-main' : 'other-main'}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/category/:category" element={<CategoryPage />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/:id" element={<OrderDetails />} />
            <Route path="/about" element={<About />} />
            <Route path="/returns" element={<ReturnPolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/terms" element={<Disclaimer />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<ProductForm />} />
              <Route path="products/:id/edit" element={<ProductForm />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:id" element={<UserDetail />} />
              <Route path="sections" element={<Sections />} />
              <Route path="sections/new" element={<SectionForm />} />
              <Route path="sections/:id/edit" element={<SectionForm />} />
              {/* Website CMS Routes */}
              <Route path="cms" element={<CMSDashboard />} />
              <Route path="cms/hero" element={<HeroManager />} />
              <Route path="cms/banners" element={<BannerManager />} />
              <Route path="cms/categories" element={<CategoryManager />} />
              <Route path="cms/featured" element={<FeaturedCollectionManager />} />
              <Route path="cms/homepage" element={<HomepageManager />} />
              <Route path="cms/about" element={<AboutManager />} />
              <Route path="cms/testimonials" element={<TestimonialManager />} />
              <Route path="cms/newsletter" element={<NewsletterManager />} />
              <Route path="cms/footer" element={<FooterManager />} />
              <Route path="cms/navigation" element={<NavigationManager />} />
              <Route path="cms/media" element={<MediaLibrary />} />
              <Route path="cms/coupons" element={<CouponManager />} />
              <Route path="cms/settings" element={<SiteSettings />} />
            </Route>
          </Routes>
        </Suspense>
      </main>
      {!isAdminRoute && (
        <Suspense fallback={<LoadingSpinner />}>
          <Footer />
        </Suspense>
      )}
      {!isAdminRoute && <BottomNav />}
      <ScrollToTopButton />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ToastProvider>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
